import { AIClient } from "@/ai/AIAssistant";
import { ApiService } from "@/api";
import { failure, isOk, ok, pending, Result } from "@/common/common";
import { BooleanFieldModel, StringFieldModel } from "@/common/commonForms";
import { CustomRoleItem, hasModuleEnabled } from "@/common/customRole";
import { ModuleName } from "@/common/product";
import { IconName } from "@/components-v2/iconMapping";
import { Language, toSupportedLanguage } from "@/i18n/languages";
import { TMessage } from "@/i18n/tt";
import { getRoleNameByLanguage } from "@/portfolio/contacts/customRoleDomain";
import AlertMessageStore from "@/stores/AlertMessageStore";
import UserStore from "@/stores/UserStore";
import { LocalizedTextEditorModel } from "@/text/localizedTextEditorDomain";
import { getFullName } from "@/utils/Roles";
import {
  resolveUrnReference,
  toCompanyRoleReference,
  toCustomRoleReference,
  toUserReference,
} from "@/utils/UrnReference";
import { newUuid } from "@/utils/uuid";
import { uniqBy } from "lodash";
import { makeAutoObservable } from "mobx";
import { GranteeAccess } from "./Settings";

export enum PermissionPrivilege {
  READ = "READ",
  WRITE = "WRITE",
  OWNER = "OWNER",
}

export const privilegeRank: Record<PermissionPrivilege, number> = {
  [PermissionPrivilege.READ]: 0,
  [PermissionPrivilege.WRITE]: 1,
  [PermissionPrivilege.OWNER]: 2,
};

export const permissionPrivilegeLabel = (privilege: PermissionPrivilege): TMessage => {
  switch (privilege) {
    case PermissionPrivilege.READ:
      return { key: "permissions.can-view" };
    case PermissionPrivilege.WRITE:
    case PermissionPrivilege.OWNER:
      return { key: "permissions.can-edit" };
  }
};

export type GetPermissionsResponse = {
  permissions: ObjectPermission[];
  admins: GranteeAccess[];
  roleItems: CustomRoleItem[];
  grantees: GranteeLabel[];
};

export type GranteeLabel = {
  grantee: string;
  label: string;
  memberCount?: number;
};

export type GranteeGroup = {
  grantee: string;
  label: TMessage;
  memberCount: number;
  icon: IconName;
};

export const findGranteeGroup = (
  grantees: GranteeLabel[],
  grantee: string,
): GranteeGroup | undefined => {
  const found = grantees.find((g) => g.grantee === grantee);
  if (found?.memberCount === undefined) return undefined;
  switch (resolveUrnReference(grantee)?.type) {
    case "companyAdmins":
    case "customRole":
      return {
        grantee,
        label: granteeLabel({ grantee, name: found.label }),
        memberCount: found.memberCount,
        icon: granteeGroupIcon,
      };
  }
};

export const granteeLabel = ({ grantee, name }: { grantee: string; name?: string }): TMessage => {
  switch (resolveUrnReference(grantee)?.type) {
    case "companyAdmins":
      return { key: "permissions.group.company-admins" };
    default:
      return { translated: name ?? "" };
  }
};

const granteeGroupIcon: IconName = "folder-user";
const granteeUserIcon: IconName = "user-circle";

export const granteeIcon = (grantee: string): IconName => {
  switch (resolveUrnReference(grantee)?.type) {
    case "companyAdmins":
    case "customRole":
      return granteeGroupIcon;
    default:
      return granteeUserIcon;
  }
};

export type GroupMember = { id: string; name: string };

export const groupMembers = (
  grantee: string,
  admins: GranteeAccess[],
  roleItems: CustomRoleItem[],
): GroupMember[] => {
  const ref = resolveUrnReference(grantee);
  switch (ref?.type) {
    case "user":
      return [{ id: ref.id, name: admins.find((a) => a.id === ref.id)?.name ?? "" }];
    case "companyAdmins":
      return admins.filter((a) => !a.isRevoked).map((a) => ({ id: a.id, name: a.name }));
    case "customRole":
      return (roleItems.find((r) => r.role.customRoleId === ref.id)?.availableUsers ?? []).map(
        (u) => ({ id: u.externalId, name: u.name }),
      );
    default:
      return [];
  }
};

export const groupMemberNames = (
  grantee: string,
  admins: GranteeAccess[],
  roleItems: CustomRoleItem[],
  language: Language,
): string[] =>
  groupMembers(grantee, admins, roleItems)
    .map((m) => m.name)
    .toSorted((a, b) => a.localeCompare(b, language));

export const countMembers = (
  grantees: string[],
  admins: GranteeAccess[],
  roleItems: CustomRoleItem[],
): number =>
  new Set(grantees.flatMap((g) => groupMembers(g, admins, roleItems).map((m) => m.id))).size;

export const groupMembersHeading = (group: GranteeGroup): TMessage => ({
  key: "permissions.group.members-in-group",
  params: { role: group.label },
});

export type InviteOption = { grantee: string; label: TMessage };

const byName =
  (language: Language) =>
  (a: { name: string }, b: { name: string }): number =>
    a.name.localeCompare(b.name, language);

const includesTerm = (name: string, searchTerm: string): boolean =>
  name.toLowerCase().includes(searchTerm);

export const inviteGroups = (
  companyAdmins: string | undefined,
  roleItems: CustomRoleItem[],
  isGranted: (grantee: string) => boolean,
  searchTerm: string,
  language: Language,
): InviteOption[] => {
  const roles = roleItems
    .map((r) => ({
      grantee: toCustomRoleReference(r.role.customRoleId),
      name: getRoleNameByLanguage(r.role, language),
    }))
    .filter((r) => !isGranted(r.grantee) && includesTerm(r.name, searchTerm))
    .sort(byName(language))
    .map<InviteOption>((r) => ({ grantee: r.grantee, label: { translated: r.name } }));
  return companyAdmins && !isGranted(companyAdmins)
    ? [{ grantee: companyAdmins, label: { key: "permissions.group.company-admins" } }, ...roles]
    : roles;
};

export const inviteUsers = (
  admins: GranteeAccess[],
  roleItems: CustomRoleItem[],
  isGranted: (grantee: string) => boolean,
  searchTerm: string,
  language: Language,
): InviteOption[] =>
  uniqBy(
    [
      ...admins.filter((a) => !a.isRevoked).map((a) => ({ id: a.id, name: a.name })),
      ...roleItems.flatMap((r) =>
        r.availableUsers.map((u) => ({ id: u.externalId, name: u.name })),
      ),
    ],
    (u) => u.id,
  )
    .filter((u) => !isGranted(toUserReference(u.id)) && includesTerm(u.name, searchTerm))
    .sort(byName(language))
    .slice(0, 10)
    .map((u) => ({ grantee: toUserReference(u.id), label: { translated: u.name } }));

export type ObjectPermission = {
  id: string;
  objectId: string;
  grantee: string;
  privilege: PermissionPrivilege;
  deleted: boolean;
};

export type ChangePermissionsRequest = {
  objectId: string;
  add: ObjectPermission[];
  change: ObjectPermission[];
  remove: ObjectPermission[];
  notifyNewPermissions: boolean;
  emailBody?: string;
};

export const privilegeLabels: { [key in PermissionPrivilege]: TMessage } = {
  [PermissionPrivilege.READ]: { key: "permission.privilege.read" },
  [PermissionPrivilege.WRITE]: { key: "permission.privilege.write" },
  [PermissionPrivilege.OWNER]: { key: "permission.privilege.write" },
};

export class ObjectPermissionModel {
  id: string;
  grantee: string;
  privilege: PermissionPrivilege;
  deleted: boolean = false;
  isExistingPermission: boolean;
  initialPermission: ObjectPermission;

  constructor(permission: ObjectPermission, isExisting: boolean = true) {
    this.initialPermission = permission;
    this.id = permission.id;
    this.grantee = permission.grantee;
    this.privilege = permission.privilege;
    this.isExistingPermission = isExisting;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get isOwner() {
    return this.privilege === PermissionPrivilege.OWNER;
  }

  removePermission() {
    this.deleted = true;
  }

  restorePermission() {
    this.deleted = false;
  }

  get isNew(): boolean {
    return !this.isExistingPermission && !this.deleted;
  }

  get isRemoved(): boolean {
    return this.isExistingPermission && this.deleted;
  }

  get isChanged() {
    return (
      this.isExistingPermission &&
      !this.deleted &&
      this.privilege !== this.initialPermission.privilege
    );
  }

  toObjectPermission(): ObjectPermission {
    return {
      id: this.id,
      objectId: this.initialPermission.objectId,
      grantee: this.grantee,
      privilege: this.privilege,
      deleted: this.deleted,
    };
  }
}

export class ChangePermissionsModel {
  title: string | TMessage;
  objectId: string;
  apiService: ApiService;
  objectPermissionResult?: Result<GetPermissionsResponse, TMessage>;
  submitPermissionResult?: Result<ObjectPermission[], TMessage>;
  permissions: ObjectPermissionModel[] = [];
  notifyNewPermissions: BooleanFieldModel;
  emailBody: LocalizedTextEditorModel;
  searchInvitePermission: StringFieldModel;

  constructor(
    title: string | TMessage,
    apiService: ApiService,
    objectId: string,
    readonly onUpdate: (permissions: ObjectPermission[]) => void,
    readonly onClose: () => void,
    readonly messageStore: AlertMessageStore,
    readonly userStore: UserStore,
    readonly aiClient: AIClient,
    readonly moduleName: ModuleName,
  ) {
    this.title = title;
    this.apiService = apiService;
    this.objectId = objectId;
    this.notifyNewPermissions = new BooleanFieldModel();
    this.emailBody = new LocalizedTextEditorModel({
      texts: [{ text: "", language: Language.NO, translationOption: { autoTranslate: false } }],
      isReadOnly: () => false,
      label: { key: "customize-message" },
      aiClient: aiClient,
    });
    this.searchInvitePermission = new StringFieldModel();
    makeAutoObservable(this, { apiService: false, onClose: false }, { autoBind: true });
  }

  get orgNo() {
    return this.userStore.orgNumber;
  }

  get companyName() {
    return this.userStore.companyName;
  }

  get language(): Language {
    return toSupportedLanguage(this.userStore.user?.language);
  }

  groupMemberNames(grantee: string): string[] {
    return isOk(this.objectPermissionResult)
      ? groupMemberNames(
          grantee,
          this.admins,
          this.objectPermissionResult.data.roleItems,
          this.language,
        )
      : [];
  }

  *fetchPermissions() {
    try {
      this.objectPermissionResult = pending();
      const response: GetPermissionsResponse = yield this.apiService.fetchPermissions(
        this.orgNo,
        this.objectId,
      );
      this.objectPermissionResult = ok(response);
      this.onUpdate(response.permissions);
      this.permissions = response.permissions.map((p) => new ObjectPermissionModel(p));
    } catch (e) {
      this.objectPermissionResult = failure({ key: "general-error" });
    }
  }

  get currentPermissions(): ObjectPermissionModel[] {
    return this.permissions
      .filter(
        (d) =>
          !d.deleted &&
          (this.findGranteeFromPermission(d.grantee) || this.findGranteeGroup(d.grantee)),
      )
      .sort((a, b) => Number(b.isOwner) - Number(a.isOwner));
  }

  findGranteeFromPermission(grantee: string): GranteeAccess | undefined {
    const admin = this.admins.find((admin) => grantee === toUserReference(admin.id));
    if (!admin || admin.isRevoked) {
      const viaRole = this.usersViaCustomRoles.find((g) => grantee === toUserReference(g.id));
      if (viaRole && !viaRole.isRevoked) {
        return viaRole;
      }
    }
    if (!admin) {
      const companyRole = (this.userStore.companyInfo?.roles ?? []).find(
        (r) => grantee === toCompanyRoleReference(r.companyroleId),
      );
      if (companyRole) {
        return {
          id: String(companyRole.companyroleId),
          name: getFullName(companyRole),
          email: companyRole.email ?? "",
          isRevoked: false,
        };
      }
    }
    return admin;
  }

  findGranteeGroup(grantee: string): GranteeGroup | undefined {
    return isOk(this.objectPermissionResult)
      ? findGranteeGroup(this.objectPermissionResult.data.grantees, grantee)
      : undefined;
  }

  get admins(): GranteeAccess[] {
    return isOk(this.objectPermissionResult) ? this.objectPermissionResult.data.admins : [];
  }

  get usersViaCustomRoles(): GranteeAccess[] {
    if (isOk(this.objectPermissionResult)) {
      const admins = this.objectPermissionResult.data.admins.map((p) => p.id);
      return this.objectPermissionResult.data.roleItems
        .map((r) =>
          r.availableUsers
            .filter((u) => !admins.includes(u.externalId))
            .map<GranteeAccess>((u) => ({
              id: u.externalId,
              name: u.name,
              email: u.email ?? "",
              isRevoked: false,
            })),
        )
        .flatMap((u) => u);
    } else {
      return [];
    }
  }

  get numberOfCustomRoles() {
    return this.archiveEnabledCustomRoles.reduce((sum, a) => sum + a.availableUsers.length, 0) ?? 0;
  }

  get isSoleUserWithRole() {
    return this.admins.length === 1 && this.numberOfCustomRoles === 0;
  }

  get searchTerm(): string {
    return (this.searchInvitePermission.inputValue ?? "").toLowerCase();
  }

  get archiveEnabledCustomRoles(): CustomRoleItem[] {
    if (!isOk(this.objectPermissionResult)) return [];
    return this.objectPermissionResult.data.roleItems.filter((r) =>
      hasModuleEnabled(r.role, this.moduleName),
    );
  }

  get customRolesWithArchiveDisabled(): string[] {
    if (!isOk(this.objectPermissionResult)) return [];
    return this.objectPermissionResult.data.roleItems
      .filter((r) => !hasModuleEnabled(r.role, this.moduleName) && r.availableUsers.length > 0)
      .map((r) => getRoleNameByLanguage(r.role, this.language));
  }

  private hasGrantee(grantee: string): boolean {
    return this.currentPermissions.some((p) => p.grantee === grantee);
  }

  private get companyAdminsGrantee(): string | undefined {
    if (!isOk(this.objectPermissionResult)) return undefined;
    return this.objectPermissionResult.data.grantees.find(
      (g) => resolveUrnReference(g.grantee)?.type === "companyAdmins",
    )?.grantee;
  }

  get invitableGroups(): InviteOption[] {
    return inviteGroups(
      this.companyAdminsGrantee,
      this.archiveEnabledCustomRoles,
      this.hasGrantee,
      this.searchTerm,
      this.language,
    );
  }

  get invitableUsers(): InviteOption[] {
    return inviteUsers(
      this.admins,
      this.archiveEnabledCustomRoles,
      this.hasGrantee,
      this.searchTerm,
      this.language,
    );
  }

  private emailFieldCache = new Map<string, StringFieldModel>();

  emailFieldFor(permission: ObjectPermissionModel): StringFieldModel {
    if (!this.emailFieldCache.has(permission.grantee)) {
      const grantee = this.findGranteeFromPermission(permission.grantee);
      this.emailFieldCache.set(
        permission.grantee,
        new StringFieldModel({ value: grantee?.email ?? "", required: !permission.isOwner }),
      );
    }
    return this.emailFieldCache.get(permission.grantee)!;
  }

  get hasInvitableItems() {
    const roles = this.archiveEnabledCustomRoles;
    return (
      inviteGroups(this.companyAdminsGrantee, roles, this.hasGrantee, "", this.language).length >
        0 || inviteUsers(this.admins, roles, this.hasGrantee, "", this.language).length > 0
    );
  }

  addPermission(admin: GranteeAccess) {
    this.addGrantee(toUserReference(admin.id));
  }

  addGrantee(grantee: string) {
    const permission = this.permissions.find((permission) => permission.grantee === grantee);
    if (permission !== undefined) {
      permission.restorePermission();
    } else {
      this.permissions.push(new ObjectPermissionModel(this.toNewPermission(grantee), false));
    }
  }

  private toNewPermission(grantee: string): ObjectPermission {
    return {
      id: newUuid(),
      objectId: this.objectId,
      grantee,
      privilege: PermissionPrivilege.WRITE,
      deleted: false,
    };
  }

  *submitPermissions() {
    try {
      this.submitPermissionResult = pending();
      const result: ObjectPermission[] = yield this.apiService.changePermissions(
        this.orgNo,
        this.toChangePermissionRequest(),
      );
      this.submitPermissionResult = ok(result);
      this.onUpdate(result);
      this.messageStore.showSuccess({
        key: "permissions.submit.success",
        params: { name: this.title },
      });
      this.onClose();
    } catch (e) {
      this.submitPermissionResult = failure({ key: "general-error" });
    }
  }

  get numberOfNewPermissions() {
    return isOk(this.objectPermissionResult)
      ? countMembers(
          this.newPermissions.map((p) => p.grantee),
          this.admins,
          this.objectPermissionResult.data.roleItems,
        )
      : 0;
  }

  get newPermissions() {
    return this.permissions.filter((permission) => permission.isNew);
  }

  get changedPermissions() {
    return this.permissions.filter((permission) => permission.isChanged);
  }

  get removedPermissions() {
    return this.permissions.filter((permission) => permission.isRemoved);
  }

  get isSubmitDisabled() {
    return (
      this.newPermissions.length === 0 &&
      this.changedPermissions.length === 0 &&
      this.removedPermissions.length === 0
    );
  }

  toChangePermissionRequest(): ChangePermissionsRequest {
    return {
      objectId: this.objectId,
      add: this.newPermissions.map((permission) => permission.toObjectPermission()),
      change: this.changedPermissions.map((permission) => permission.toObjectPermission()),
      remove: this.removedPermissions.map((permission) => permission.toObjectPermission()),
      notifyNewPermissions: this.notifyNewPermissions.value ?? false,
      emailBody: this.emailBody.textModels[0].model.isEmpty
        ? undefined
        : this.emailBody.textModels[0].model.inputValue,
    };
  }
}
